import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStoredUser, isAdmin } from '../../utils/auth';
import useStyles from '../../theme/useStyles';
import { useTheme } from '../../theme/ThemeContext';
import { useNotification } from '../../hooks';
import { PageHeader, TablePanel, Notification, Drawer, Button, Badge, PromptDialog } from '../../components/ui';
import type { Column } from '../../components/ui';
import { approvals as approvalsApi } from '../../api/endpoints';
import type { ApprovalBox, ApprovalView } from '../../api/endpoints';
import { ApiError } from '../../api/client';
import ApprovalProgress from './ApprovalProgress';
import { MODULES } from '../../utils/constants';

// The Approvals inbox: what is waiting for me, what I asked for, and what was
// decided. Approve or reject from here; for a registration, open it first to
// see the whole form. The server decides who may act (Settings › Approvals).

const TABS: Array<{ key: ApprovalBox; label: string }> = [
  { key: 'waiting', label: '🕒 Waiting for me' },
  { key: 'mine', label: '📤 My requests' },
  { key: 'history', label: '📜 History' }
];

const when = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** Withdrawn — also one withdrawn before WITHDRAWN existed (closed as CANCELLED with that note). */
const isWithdrawn = (a: ApprovalView) => a.status === 'WITHDRAWN'
  || (a.status === 'CANCELLED' && /^Withdrawn by /.test(a.decisionComment || ''));

const statusBadge = (a: ApprovalView) =>
  a.status === 'APPROVED' ? <Badge tone="success">Approved</Badge>
    : a.status === 'REJECTED' ? <Badge tone="error">Rejected</Badge>
      : isWithdrawn(a) ? <Badge tone="neutral">Withdrawn</Badge>
        : a.status === 'CANCELLED' ? <Badge tone="neutral">Cancelled</Badge>
          : <Badge tone="warning">Level {a.nextLevel} of {a.levelsRequired}</Badge>;

/** Who closed it, with their role. */
const decider = (a: ApprovalView) =>
  a.decidedBy ? `${a.decidedBy}${a.decidedByRole ? ` (${a.decidedByRole})` : ''}` : '—';

/** What each tab is for — shown in a coloured box so the tabs are told apart at a glance. */
const TAB_INTRO: Record<string, { text: string; tone: 'warning' | 'primary' | 'success' }> = {
  waiting: { tone: 'warning', text: 'Requests at a level you may approve. Levels go in order; each level needs a different person, and you cannot approve your own request.' },
  mine: { tone: 'primary', text: 'Requests YOU made — waiting, approved, rejected or withdrawn — and where each one is now. Open one to withdraw it, or to correct and send again a rejected new record.' },
  history: { tone: 'success', text: 'The record of decisions in the parts of the app you can see: every approved, rejected, withdrawn and cancelled request — who decided, when, and their remark.' }
};

/** Where to open the record a request is about, when there is a screen for it. */
const recordLink = (a: ApprovalView) => {
  // A family added by the office ("Add Head") is a registration (REG-…) until approved.
  const registration = a.type === 'REGISTRATION'
    || (a.module === 'Members & Families' && a.type.startsWith('CREATE:') && /^REG-/i.test(a.reference || ''));
  if (registration) return a.recordId == null ? null : { path: '/congregation', state: { openRegistrationId: a.recordId } };
  // A create still waiting, a batch ("Several changes") or a held import has no record yet.
  if (a.recordId == null) return null;
  const moduleId = a.type === 'BUDGET' || a.type === 'BUDGET_ADJUSTMENT' ? 'Budget Management' : a.module;
  const mod = MODULES.find((m) => m.id === moduleId);
  return mod
    ? { path: `/${mod.route}`, state: { openRecordId: a.recordId, openRequestType: a.type, openRecordRef: a.reference } }
    : null;
};

const ApprovalsInbox = () => {
  const { isDarkMode } = useTheme();
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const navigate = useNavigate();
  const { notification, notify, dismiss } = useNotification();

  const [box, setBox] = useState<ApprovalBox>('waiting');
  const [rows, setRows] = useState<ApprovalView[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<ApprovalView | null>(null);
  const [busy, setBusy] = useState(false);
  const [askReject, setAskReject] = useState(false);
  // Corrections to a rejected new record, field -> new value.
  const [edits, setEdits] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await approvalsApi.list(box)) || []);
    } catch (err) {
      notify((err as Error).message || 'Could not load approvals.', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box]);

  useEffect(() => { load(); }, [load]);

  // Opened from a module's strip: show that request straight away.
  const location = useLocation();
  useEffect(() => {
    const openId = (location.state as { openId?: number } | null)?.openId;
    if (openId) approvalsApi.get(openId).then((a) => { setOpen(a); setEdits({}); }).catch(() => {});
  }, [location.state]);

  const me = getStoredUser();
  const [askWithdraw, setAskWithdraw] = useState(false);
  const canWithdraw = Boolean(open && open.status === 'PENDING'
    && (isAdmin(me) || String(open.requestedBy || '').toLowerCase() === String(me?.username || '').toLowerCase()));

  const withdraw = async (reason: string) => {
    setAskWithdraw(false);
    if (!open) return;
    setBusy(true);
    try {
      await approvalsApi.withdraw(open.id, reason);
      afterDecision('Withdrawn. Nothing was carried out; a delete request puts the record back. It is listed in History as Withdrawn.');
      window.dispatchEvent(new Event('approvals-applied'));
    } catch (err) {
      handleError(err, 'Could not withdraw.');
    } finally {
      setBusy(false);
    }
  };

  const afterDecision = (message: string) => {
    notify(message, 'success');
    setOpen(null);
    load();
    window.dispatchEvent(new Event('approvals-changed'));
  };

  const handleError = async (err: unknown, fallback: string) => {
    notify((err as Error).message || fallback, 'error');
    if (err instanceof ApiError && err.status === 409 && open) {
      try { setOpen(await approvalsApi.get(open.id)); } catch { /* list reload still runs */ }
      load();
    }
  };

  const [askApprove, setAskApprove] = useState(false);

  const approve = async (remark: string) => {
    setAskApprove(false);
    if (!open) return;
    setBusy(true);
    try {
      const done = await approvalsApi.approve(open.id, open.version, remark.trim() || undefined);
      afterDecision(done.status === 'APPROVED'
        ? `${done.typeLabel} ${done.reference || ''} fully approved and carried out.`
        : `Level ${done.approvedLevels} approved. Now waiting for level ${done.nextLevel}.`);
    } catch (err) {
      handleError(err, 'Could not approve.');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (reason: string) => {
    setAskReject(false);
    if (!open) return;
    setBusy(true);
    try {
      await approvalsApi.reject(open.id, open.version, reason);
      afterDecision('Rejected. The requester will see your reason.');
    } catch (err) {
      handleError(err, 'Could not reject.');
    } finally {
      setBusy(false);
    }
  };

  // Each tab answers a different question, so each has its own columns.
  const what: Column<ApprovalView> = { key: 'typeLabel', header: 'What', render: (a) => <strong>{a.typeLabel}</strong> };
  const record: Column<ApprovalView> = {
    key: 'reference', header: 'Record',
    render: (a) => (
      <>
        <span style={{ fontFamily: 'monospace', fontWeight: t.fontWeight.bold }}>{a.reference}</span>
        {a.summary && <div style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>{a.summary}</div>}
      </>
    )
  };
  const remark: Column<ApprovalView> = {
    key: 'decisionComment', header: 'Remark', wrap: true, maxWidth: '280px',
    render: (a) => (a.decisionComment
      ? <span style={{ whiteSpace: 'pre-wrap' }}>{a.decisionComment}</span>
      : <span style={{ color: t.textMuted }}>—</span>)
  };
  const columns: Array<Column<ApprovalView>> = box === 'history' ? [
    what, record,
    { key: 'status', header: 'Outcome', nowrap: true, render: statusBadge },
    { key: 'decidedBy', header: 'Decided by', render: decider },
    { key: 'closedAt', header: 'Decided on', nowrap: true, render: (a) => when(a.closedAt) },
    remark,
    { key: 'requestedBy', header: 'Requested by', nowrap: true, render: (a) => `${a.requestedBy || '—'} · ${when(a.requestedAt)}` }
  ] : box === 'mine' ? [
    what, record,
    { key: 'status', header: 'Status', nowrap: true, render: statusBadge },
    {
      key: 'waitingFor', header: 'Now with',
      render: (a) => (a.status === 'PENDING'
        ? <>Waiting for {a.waitingFor.join(' or ')}</>
        : <>{isWithdrawn(a) ? 'Withdrawn by' : a.status === 'CANCELLED' ? 'Closed' : 'Decided by'} {decider(a)} · {when(a.closedAt)}</>)
    },
    remark,
    { key: 'requestedAt', header: 'Sent on', nowrap: true, render: (a) => when(a.requestedAt) }
  ] : [
    what, record,
    { key: 'status', header: 'Level', nowrap: true, render: statusBadge },
    { key: 'requestedBy', header: 'Requested by', nowrap: true, render: (a) => a.requestedBy || '—' },
    { key: 'requestedAt', header: 'Requested on', nowrap: true, render: (a) => when(a.requestedAt) }
  ];
  const intro = TAB_INTRO[box] || TAB_INTRO.waiting;
  const introColor = intro.tone === 'warning' ? t.warning : intro.tone === 'success' ? t.success : t.primaryText;

  const link = open ? recordLink(open) : null;
  // My own rejected new record: it can be corrected and sent again.
  const canResubmit = Boolean(open && box === 'mine' && open.status === 'REJECTED' && open.type.startsWith('CREATE:'));

  const resubmit = async () => {
    if (!open) return;
    setBusy(true);
    try {
      const again = await approvalsApi.resubmit(open.id, edits);
      setEdits({});
      afterDecision(`Sent again for approval (level 1 of ${again.levelsRequired}).`);
    } catch (err) {
      notify((err as Error).message || 'Could not send it again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />
      <PageHeader
        icon="✅"
        title="Approvals"
        activeTab={box}
        onTabChange={(k) => setBox(k as ApprovalBox)}
        tabs={TABS}
        isDarkMode={isDarkMode}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: t.spacing.md, flexWrap: 'wrap' }}>
        <div style={{
          flex: '1 1 400px', maxWidth: '860px', padding: '8px 12px', borderRadius: t.radius.md,
          borderLeft: `4px solid ${introColor}`, backgroundColor: t.bgSubtle, color: t.textMain, fontSize: t.fontSize.sm
        }}>
          <strong style={{ color: introColor }}>{TABS.find((x) => x.key === box)?.label}</strong>
          <div style={{ color: t.textMuted, marginTop: '2px' }}>{intro.text}</div>
        </div>
        <Button variant="ghost" size="sm" isDarkMode={isDarkMode} onClick={load}>↻ Refresh</Button>
      </div>

      <TablePanel
        title={TABS.find((x) => x.key === box)?.label.replace(/^\S+\s/, '') || 'Approvals'}
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        onRowClick={(a) => { setOpen(a); setEdits({}); }}
        emptyMessage={box === 'waiting' ? 'Nothing is waiting for you. 🎉' : 'Nothing here yet.'}
      />

      {open && (
        <Drawer
          open
          width="640px"
          title={`${open.typeLabel} · ${open.reference || ''}`}
          subtitle={open.summary}
          onClose={busy ? undefined : () => setOpen(null)}
          isDarkMode={isDarkMode}
          footer={(
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {link && (
                <Button variant="secondary" isDarkMode={isDarkMode} onClick={() => navigate(link.path, { state: link.state })}>
                  Open the record
                </Button>
              )}
              {canWithdraw && (
                <Button variant="secondary" isDarkMode={isDarkMode} disabled={busy} onClick={() => setAskWithdraw(true)}>
                  ↩ Withdraw
                </Button>
              )}
              {canResubmit && (
                <button type="button" disabled={busy} style={styles.submitBtn} onClick={resubmit}>
                  ↻ Send again{Object.keys(edits).length ? ' with corrections' : ''}
                </button>
              )}
              {open.canAct && (
                <>
                  <button type="button" disabled={busy} style={styles.dangerBtn} onClick={() => setAskReject(true)}>✕ Reject</button>
                  <button type="button" disabled={busy} style={styles.submitBtn} onClick={() => setAskApprove(true)}>
                    ✓ Approve level {open.nextLevel}{open.nextLevel === open.levelsRequired ? ' (final)' : ''}
                  </button>
                </>
              )}
            </div>
          )}
        >
          {open.status === 'PENDING' && !open.canAct && open.whyNot && (
            <div style={{
              marginBottom: '12px', padding: '10px 12px', borderRadius: t.radius.md,
              border: `1px solid ${t.border}`, backgroundColor: t.bgRaised, color: t.textMuted, fontSize: t.fontSize.sm
            }}>
              ℹ Why there is no Approve button: {open.whyNot}
            </div>
          )}
          <ApprovalProgress approval={open} t={t} />
          {open.details && open.details.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <strong style={{ color: t.textMain }}>
                {open.type.startsWith('EDIT:') ? (open.status === 'APPROVED' ? 'What was changed' : 'What would change')
                  : open.status === 'APPROVED' ? 'The record that was added'
                    : canResubmit ? 'Correct what was rejected, then send it again' : 'The record waiting to be added'}
              </strong>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px', fontSize: t.fontSize.sm }}>
                <tbody>
                  {open.details.map((d) => (
                    <tr key={d.field} style={{ borderTop: `1px solid ${t.border}` }}>
                      <td style={{ padding: '4px 8px', color: t.textMuted, width: '40%' }}>
                        {d.field.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
                      </td>
                      <td style={{ padding: '4px 8px', color: t.textMain }}>
                        {canResubmit && d.value !== '(attached file)' && !d.value.endsWith('...') ? (
                          <input
                            value={edits[d.field] ?? d.value}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [d.field]: e.target.value }))}
                            style={{ width: '100%', padding: '3px 6px', border: `1px solid ${t.border}`, borderRadius: t.radius.sm, background: t.bgInput, color: t.textMain }}
                          />
                        ) : d.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {open.canAct && open.nextLevel === open.levelsRequired && (
            <p style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>
              This is the last level: approving carries the action out
              {open.type === 'REGISTRATION' ? ' — the family and its members are added to Members & Families.'
                : open.type.startsWith('DELETE:') ? ' — the record is deleted (it can still be restored from the Deleted tab).'
                  : open.type.startsWith('CREATE:') ? ' — the record is added to the module.'
                    : open.type.startsWith('EDIT:') ? ' — the change is applied to the record.' : '.'}
            </p>
          )}
        </Drawer>
      )}

      <PromptDialog
        open={askWithdraw}
        title="Withdraw this request"
        message="It stops here and nothing is carried out. A delete request puts the record back as it was."
        placeholder="Reason (optional)"
        confirmLabel="Withdraw"
        required={false}
        onSubmit={withdraw}
        onCancel={() => setAskWithdraw(false)}
        isDarkMode={isDarkMode}
      />

      <PromptDialog
        open={askApprove}
        title={open ? `Approve level ${open.nextLevel}${open.nextLevel === open.levelsRequired ? ' (final)' : ''}` : 'Approve'}
        message="Add a remark if you like. It is kept in the history with your approval."
        placeholder="Remark (optional)"
        confirmLabel="✓ Approve"
        required={false}
        destructive={false}
        onSubmit={approve}
        onCancel={() => setAskApprove(false)}
        isDarkMode={isDarkMode}
      />

      <PromptDialog
        open={askReject}
        title="Reject"
        message="Give a reason. The request stops here and the requester sees why."
        placeholder="Reason"
        confirmLabel="Reject"
        required
        requiredError="Enter a reason."
        multiline
        destructive
        onSubmit={reject}
        onCancel={() => setAskReject(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default ApprovalsInbox;
